import { useState } from 'react';
import { ChevronDown, ChevronRight, Brain, BarChart2, PlayCircle, Users, Loader2, Check } from 'lucide-react';
import clsx from 'clsx';
import { getAgentConfig, type AgentConfig } from '../../config/agentRegistry';
import type { SubAgentBlock as SubAgentBlockType, ThinkingPhase } from '../../types/chat';
import { NestedToolCallBlock } from './NestedToolCallBlock';

interface SubAgentBlockProps {
  block: SubAgentBlockType;
}

interface ThinkingPhaseBlockProps {
  phase: ThinkingPhase;
  config: AgentConfig;
  isLastPhase: boolean;
  isAgentRunning: boolean;
}

// Map icon names to components
const ICONS = {
  'brain': Brain,
  'chart-bar': BarChart2,
  'play-circle': PlayCircle,
  'users': Users,
};

/**
 * Detects if thinking content is just markdown code fence artifacts.
 * These appear when the agent starts a JSON response with ```json but
 * the backticks arrive in chunks before the JSON content filter kicks in.
 */
function isOnlyMarkdownArtifact(text: string): boolean {
  const cleaned = text.trim();
  // Match patterns like: ```, ```json, ``` json, just "json", or whitespace
  return /^(`{1,3}\s*(json)?|json\s*`{0,3})?\s*$/i.test(cleaned) || cleaned === '';
}

/**
 * Strips trailing markdown code fence artifacts from thinking content.
 * Returns the cleaned text and whether artifacts were found.
 * Handles patterns like "...analysis:\n\n```" or "...analysis:\n\n```json"
 */
function stripTrailingMarkdownArtifacts(text: string): { cleaned: string; hadArtifacts: boolean } {
  // Match trailing patterns: newlines/spaces followed by ```, ```json, or just backticks
  const trailingPattern = /[\s\n]*`{1,3}\s*(json)?\s*$/i;
  const match = text.match(trailingPattern);

  if (match) {
    return {
      cleaned: text.slice(0, -match[0].length).trimEnd(),
      hadArtifacts: true
    };
  }

  return { cleaned: text, hadArtifacts: false };
}

/**
 * Renders a single thinking phase with its associated tools.
 */
function ThinkingPhaseBlock({ phase, config, isLastPhase, isAgentRunning }: ThinkingPhaseBlockProps) {
  const showStreamingIndicator = isLastPhase && isAgentRunning && phase.tools.length === 0;
  const hasContent = phase.thinking || phase.tools.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <div className="border-b last:border-b-0">
      {/* Thinking content - only render if there's thinking text */}
      {phase.thinking && (
        <div className={clsx('px-3 py-2', config.colors.bgLight)}>
          <div className="flex items-start gap-2">
            <Brain className={clsx('w-3 h-3 mt-1 flex-shrink-0', config.colors.text)} />
            <span className={clsx('text-xs whitespace-pre-wrap', config.colors.text)}>
              {(() => {
                // Handle markdown artifacts in the last phase while agent is running
                if (isLastPhase && isAgentRunning) {
                  // Case 1: Entire content is just artifacts (e.g., "```" or "```json")
                  if (isOnlyMarkdownArtifact(phase.thinking)) {
                    return (
                      <span className="flex items-center gap-1">
                        <span className="italic">Processing response</span>
                        <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: 'currentColor' }} />
                        <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: 'currentColor', animationDelay: '150ms' }} />
                        <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: 'currentColor', animationDelay: '300ms' }} />
                      </span>
                    );
                  }

                  // Case 2: Real content ending with artifacts (e.g., "Let me analyze:\n\n```")
                  const { cleaned, hadArtifacts } = stripTrailingMarkdownArtifacts(phase.thinking);
                  if (hadArtifacts) {
                    return (
                      <>
                        {cleaned.trimStart()}
                        {'\n\n'}
                        <span className="flex items-center gap-1">
                          <span className="italic">Processing response</span>
                          <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: 'currentColor' }} />
                          <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: 'currentColor', animationDelay: '150ms' }} />
                          <span className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: 'currentColor', animationDelay: '300ms' }} />
                        </span>
                      </>
                    );
                  }
                }

                // Default: show thinking content as-is
                return (
                  <>
                    {phase.thinking.trimStart()}
                    {showStreamingIndicator && (
                      <span className="inline-block w-1.5 h-3 ml-0.5 bg-current animate-pulse" />
                    )}
                  </>
                );
              })()}
            </span>
          </div>
        </div>
      )}

      {/* Tools for this phase - indented below thinking */}
      {phase.tools.length > 0 && (
        <div className="px-3 py-2 space-y-2 ml-5">
          {phase.tools.map(tool => (
            <NestedToolCallBlock key={tool.id} toolCall={tool} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Standalone display for sub-agent activity.
 * Rendered at the stream level (not nested inside delegation tool).
 * Activity is organized into phases - each phase contains thinking followed by tools.
 */
export function SubAgentBlock({ block }: SubAgentBlockProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const config = getAgentConfig(block.agentName);
  const Icon = ICONS[config.icon];
  const isRunning = block.status === 'running';

  // Check if there's any content to display
  const hasContent = block.phases.some(phase => phase.thinking || phase.tools.length > 0);

  // Don't render if there's no activity
  if (!hasContent) {
    return null;
  }

  return (
    <div className={clsx('mb-3 rounded-lg border-2 overflow-hidden', config.colors.border)}>
      {/* Header with agent name, icon, status */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx(
          'w-full px-3 py-2 flex items-center gap-2',
          config.colors.bg,
          'hover:opacity-90 transition-opacity'
        )}
      >
        <Icon className={clsx('w-4 h-4', config.colors.text)} />
        <span className={clsx('font-medium text-sm', config.colors.text)}>
          {config.displayName}
        </span>
        {isRunning ? (
          <Loader2 className={clsx('w-4 h-4 animate-spin', config.colors.text)} />
        ) : (
          <Check className="w-4 h-4 text-emerald-500" />
        )}
        <span className={clsx('ml-auto', config.colors.text)}>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </span>
      </button>

      {isExpanded && (
        <div className="bg-white">
          {block.phases.map((phase, index) => (
            <ThinkingPhaseBlock
              key={phase.id}
              phase={phase}
              config={config}
              isLastPhase={index === block.phases.length - 1}
              isAgentRunning={isRunning}
            />
          ))}
        </div>
      )}
    </div>
  );
}
